import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiHome, FiPackage, FiUsers, FiDollarSign, FiSettings, FiLogOut,
  FiMenu, FiX, FiBarChart2, FiSun, FiMoon, FiDroplet, FiMaximize, FiGrid
} from 'react-icons/fi';
import { useAuth } from '../hooks/useAuth';

const AdminNavigationShed = ({ activeTab, setActiveTab, userRole = 'master_admin', isSidebarOpen, setIsSidebarOpen }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const { user, signOut } = useAuth();

  useEffect(() => {
    const saved = localStorage.getItem('adminDarkMode');
    if (saved === 'true') {
      setDarkMode(true);
      document.body.classList.add('dark-mode');
    }
  }, []);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    if (!darkMode) {
      document.body.classList.add('dark-mode');
      localStorage.setItem('adminDarkMode', 'true');
    } else {
      document.body.classList.remove('dark-mode');
      localStorage.setItem('adminDarkMode', 'false');
    }
  };

  const isMaster = userRole === 'master_admin';

  const isDealer = userRole === 'dealer_admin' || userRole === 'dealer_staff';

  const allNavigation = [
    { id: isDealer ? 'dealer-dashboard' : 'dashboard', name: 'Dashboard', icon: FiHome, roles: ['master_admin', 'dealer_admin', 'dealer_staff'] },
    // 'Dealers & Cities' (multi-dealer management) hidden — lowessheds is single-dealer (Edwin only).
    { id: 'sheds', name: 'Shed Inventory', icon: FiPackage, roles: ['master_admin', 'dealer_admin', 'dealer_staff'] },
    { id: 'colors', name: 'Colors', icon: FiDroplet, roles: ['master_admin', 'dealer_admin'] },
    { id: 'sizes', name: 'Sizes', icon: FiMaximize, roles: ['master_admin', 'dealer_admin'] },
    { id: 'windows', name: 'Windows', icon: FiGrid, roles: ['master_admin', 'dealer_admin'] },
    { id: 'leads', name: 'Leads', icon: FiUsers, roles: ['master_admin', 'dealer_admin'] },
    { id: 'payments', name: 'Payments', icon: FiDollarSign, roles: ['master_admin', 'dealer_admin'] },
    { id: 'analytics', name: 'Analytics', icon: FiBarChart2, roles: ['master_admin'] },
    { id: 'settings', name: 'Settings', icon: FiSettings, roles: ['master_admin', 'dealer_admin'] },
  ];

  const navigation = allNavigation.filter(item => item.roles.includes(userRole));

  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/';
  };

  return (
    <>
      {/* Top Header Bar */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 z-50 flex items-center justify-between px-4">
        {/* Left side */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="hidden lg:flex p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <FiMenu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiMenu className="w-5 h-5 text-gray-600" />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-lg flex items-center justify-center">
              <FiHome className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold text-gray-900 dark:text-white">
              Shed<span className="text-emerald-600">X</span>
            </span>
          </Link>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-3">
          <button
            onClick={toggleDarkMode}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            {darkMode ? (
              <FiSun className="w-5 h-5 text-yellow-500" />
            ) : (
              <FiMoon className="w-5 h-5 text-gray-600" />
            )}
          </button>
          <Link
            to="/"
            className="hidden sm:flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <FiHome className="w-4 h-4" />
            <span className="text-sm">View Site</span>
          </Link>
          <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-slate-700 rounded-lg">
            <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-medium">
              {user?.email?.charAt(0).toUpperCase() || 'A'}
            </div>
            <span className="text-sm text-gray-700 dark:text-gray-300">{user?.email || 'Admin'}</span>
          </div>
          <button
            onClick={handleSignOut}
            className="p-2 text-gray-600 dark:text-gray-300 hover:bg-red-100 hover:text-red-600 rounded-lg transition-colors"
            title="Sign Out"
          >
            <FiLogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Desktop Sidebar */}
      <aside
        className={`fixed left-0 top-16 bottom-0 bg-white dark:bg-slate-800 border-r border-gray-200 dark:border-slate-700 transition-all duration-300 z-40 hidden lg:block ${
          isSidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        <nav className="p-4 space-y-1">
          {navigation.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === item.id
                  ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700'
              }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {isSidebarOpen && <span className="font-medium">{item.name}</span>}
            </button>
          ))}
        </nav>
      </aside>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="fixed left-0 top-0 bottom-0 w-72 bg-white dark:bg-slate-800 z-50 lg:hidden"
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-lg flex items-center justify-center">
                    <FiHome className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-bold text-gray-900 dark:text-white">Admin</span>
                </div>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                >
                  <FiX className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
              </div>
              <nav className="p-4 space-y-1">
                {navigation.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveTab(item.id);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      activeTab === item.id
                        ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.name}</span>
                  </button>
                ))}
              </nav>
              <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 dark:border-slate-700">
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  <FiLogOut className="w-5 h-5" />
                  <span className="font-medium">Sign Out</span>
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

    </>
  );
};

export default AdminNavigationShed;
